/** Macro #30 — Follow / Unfollow control (creator-scoped). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { cn } from "../cn";
import { socialApi, SocialApiError } from "../../lib/social-api";
import { useSession } from "../marketplace/useSession";
import { PILL, PILL_ACTIVE, Spinner } from "./social-ui";
import { useI18n } from "../../i18n";

interface Props {
  /** Known creator wallet — enables an accurate initial follow-state fetch. */
  creatorWallet?: string | null;
  /** Fallback target when the wallet is not known (Territory surface). */
  territoryIdentityId?: string;
  className?: string;
  onChange?: (following: boolean) => void;
}

export function FollowButton({ creatorWallet, territoryIdentityId, className, onChange }: Props) {
  const { t } = useI18n();
  const { wallet, isAuthenticated } = useSession();
  const qc = useQueryClient();
  const self = !!creatorWallet && !!wallet && creatorWallet.toLowerCase() === wallet.toLowerCase();

  const statusQ = useQuery({
    queryKey: ["social", "follow-status", creatorWallet],
    queryFn: ({ signal }) => socialApi.followStatus(creatorWallet as string, signal),
    enabled: isAuthenticated && !!creatorWallet && !self,
    staleTime: 10_000,
    retry: false,
  });

  // When only an identity is known, track local optimistic state.
  const [localFollowing, setLocalFollowing] = useState<boolean | null>(null);
  const following = creatorWallet ? !!statusQ.data?.following : !!localFollowing;

  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: async () => {
      const target = creatorWallet ? { creator_wallet: creatorWallet } : { territory_identity_id: territoryIdentityId };
      return following ? socialApi.unfollow(target) : socialApi.follow(target);
    },
    onSuccess: (res) => {
      const now = "following" in res ? res.following : !following;
      setLocalFollowing(now);
      onChange?.(now);
      if (creatorWallet) {
        qc.invalidateQueries({ queryKey: ["social", "follow-status", creatorWallet] });
        qc.invalidateQueries({ queryKey: ["social", "profile"] });
      }
      qc.invalidateQueries({ queryKey: ["social", "feed", "following"] });
    },
    onError: (e) => setErr(e instanceof SocialApiError ? e.message : t("social.follow.actionFailed")),
  });

  if (self) return null;

  if (!isAuthenticated) {
    return (
      <a href="#auth" data-testid="follow-signin" className={cn(PILL, className)}>
        {t("social.follow.follow")}
      </a>
    );
  }

  return (
    <div className="inline-flex flex-col items-start gap-1">
      <button
        type="button"
        data-testid="follow-btn"
        data-following={following ? "true" : "false"}
        disabled={mut.isPending}
        onClick={() => { setErr(null); mut.mutate(); }}
        className={cn(PILL, following && PILL_ACTIVE, className)}
      >
        {mut.isPending ? <Spinner /> : null}
        {following ? t("social.follow.following") : t("social.follow.follow")}
      </button>
      {err && <span data-testid="follow-error" className="font-mono text-[10px] text-state-danger">{err}</span>}
    </div>
  );
}
